import { Typography } from "@mui/joy";
import React, { useState } from "react";
import { useSiteContext } from "../../../context/siteContext";
import useApiService, { getCacheKey } from "../../../hooks/useApiService";
import { Tree } from "primereact/tree";
import { TreeNode } from "primereact/treenode";
import ModernLoader from "../../../components/Shared/ModernLoader";
import { Dialog } from "primereact/dialog";
import { SiteEmployee } from "./employee/SiteEmployee";
import { SiteEmployee as SiteEmployeeInterface } from "../../../models/employee.interface";

function SiteOrganogram() {
  const { site } = useSiteContext();

  const [treeData, setTreeData] = useState<TreeNode[]>();
  const [expandedKeys, setExpandedKeys] = useState<Record<string, boolean>>();
  const [modalToShow, setModalToShow] = useState<"site-employee" | null>(null);
  const [employees, setEmployees] = useState<SiteEmployeeInterface[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<SiteEmployeeInterface | undefined>(
    undefined
  );

  const cacheKey = getCacheKey({
    method: "GET",
    url: "site-organogram",
    apiName: "core",
    params: {
      siteId: site?.id,
    },
  });

  const { loading } = useApiService<any[]>({
    method: "GET",
    url: "site-organogram",
    apiName: "core",
    onSuccess: data => {
      let keys: Record<string, boolean> = {};
      const employees: any[] = [];
      const seenEmployeeIds = new Set<string>();
      const transformData = (node: any): TreeNode => {
        const children = [];

        if (node.child) {
          children.push(...node.child.map(transformData));
        }

        if (node.employee) {
          for (const emp of node.employee) {
            if (!seenEmployeeIds.has(emp.id)) {
              seenEmployeeIds.add(emp.id);
              employees.push(emp); // unique by emp.id
            }
          }
          children.push(
            ...node.employee.map((emp: any) => ({
              key: emp.id,
              label: `${emp.name} ${emp.surname}`,
              icon: "pi pi-user",
            }))
          );
        }

        keys[node.id] = true;

        return {
          key: node.id,
          label: node.name,
          children: children,
        };
      };

      const transformedData = [transformData(data)];
      setExpandedKeys(keys);
      setTreeData(transformedData);
      setEmployees(employees as SiteEmployeeInterface[]);
    },
    cache: {
      enabled: true,
      strategy: "cache-first",
      ttlMs: 5 * 60_000,
      key: cacheKey,
    },
  });

  return (
    <div>
      <Typography level="h3" component="h2">
        Organogram: {site?.name}
      </Typography>
      {loading ? (
        <div style={{ position: "relative" }}>
          <ModernLoader />
        </div>
      ) : (
        <div>
          <Tree
            value={treeData}
            selectionMode={"single"}
            filter
            filterMode={"lenient"}
            filterPlaceholder={"Search organogram"}
            className={"w-full"}
            expandedKeys={expandedKeys}
            onSelect={e => {
              setSelectedEmployee(
                employees.find(employee => employee.id === (e.node.key as string))
              );
              setModalToShow("site-employee");
            }}
          />
        </div>
      )}
      {modalToShow && selectedEmployee && (
        <Dialog
          onHide={() => setModalToShow(null)}
          style={{ width: "50vw" }}
          header="Employee Details"
          visible={modalToShow === "site-employee"}
        >
          <SiteEmployee employee={selectedEmployee} />
        </Dialog>
      )}
    </div>
  );
}

export default SiteOrganogram;
